import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import LanguageToggle from "../components/app/LanguageToggle";
import Avatar from "../components/app/Avatar";
import { Hand, ArrowLeft, Star, Clock, PlayCircle } from "lucide-react";
import Logo from "../components/app/Logo";

export default function GoalkeeperCoaches() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [c, d] = await Promise.all([
        api.get("/public/coaches", { params: { specialty: "goalkeeper" } }),
        api.get("/goalkeeper/drills"),
      ]);
      setCoaches(Array.isArray(c.data) ? c.data : (c.data?.coaches || []));
      setDrills(d.data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen pb-24">
      <header className="header-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between p-4">
          <Link to={user ? "/" : "/find-coaches"} className="flex items-center">
            <Logo size={26} wordmark />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/find-coaches" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> {lang === "bg" ? "Всички треньори" : "All coaches"}
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-10 space-y-10">
        {/* Hero */}
        <section className="relative card-sharp overflow-hidden grain animate-fade-up" style={{ opacity: 0 }}>
          <div className="spotlight" />
          <div className="absolute inset-0 pitch-field opacity-30" />
          <div className="relative p-8 md:p-14 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-pitch/40 text-pitch label-mono mb-4">
              <Hand className="w-3 h-3" /> GOALKEEPER PROGRAM
            </div>
            <h1 className="font-oswald uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">
              {lang === "bg" ? <>Стани <span className="text-pitch">№1</span><br />между гредите</> : <>The <span className="text-pitch">#1</span><br />between the posts</>}
            </h1>
            <p className="text-white/60 mt-4 max-w-xl">
              {lang === "bg"
                ? "Открий елитни треньори по вратарство, индивидуални програми и упражнения, разработени за момчета и момичета на всякаво ниво."
                : "Find elite goalkeeper coaches, individual programmes and drills designed for any age and level."}
            </p>
          </div>
        </section>

        {/* Goalkeeper coaches */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-oswald uppercase text-3xl tracking-tight">{lang === "bg" ? "Треньори по вратарство" : "Goalkeeper coaches"}</h2>
            <Link to="/find-coaches?specialty=goalkeeper" className="text-pitch text-xs font-mono uppercase tracking-widest hover:underline">
              {lang === "bg" ? "Виж всички" : "View all"} →
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><div className="pitch-loader"><div className="ball" /></div></div>
          ) : coaches.length === 0 ? (
            <div className="card-sharp p-10 text-center text-white/30 font-mono text-xs tracking-widest">
              {lang === "bg" ? "Все още няма треньори по вратарство" : "No goalkeeper coaches yet"}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {coaches.map((c) => (
                <Link key={c.id} to={`/coach/${c.id}`} className="card-sharp interactive p-6 group" data-testid={`gk-coach-${c.id}`}>
                  <div className="flex items-start gap-4">
                    <Avatar name={c.name} src={c.photo_url} size="lg" ring />
                    <div className="flex-1 min-w-0">
                      <div className="font-oswald uppercase text-lg tracking-tight truncate">{c.name}</div>
                      <div className="flex items-center gap-1.5 text-xs text-white/50 mt-0.5">
                        <Star className="w-3 h-3 text-pitch fill-pitch" />
                        <span className="font-mono">{(c.rating || 0).toFixed(1)}</span>
                        <span className="text-white/30">·</span><span>{c.review_count || 0}</span>
                      </div>
                      {c.city && <div className="text-xs text-white/40 mt-1">{c.city}</div>}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <span className="tag tag-pitch flex items-center gap-1"><Hand className="w-2.5 h-2.5" /> GOALKEEPER</span>
                    {(c.badges || []).includes("top_rated") && <span className="tag tag-pitch">TOP RATED</span>}
                    {(c.badges || []).includes("uefa_a") && <span className="tag">UEFA A</span>}
                    {(c.badges || []).includes("uefa_pro") && <span className="tag tag-pitch">UEFA PRO</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Drills */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-oswald uppercase text-3xl tracking-tight">{lang === "bg" ? "Упражнения" : "Drill library"}</h2>
            <span className="label-mono">{drills.length} {lang === "bg" ? "Упражнения" : "drills"}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {drills.map((d, i) => (
              <div
                key={d.id}
                className="card-sharp interactive p-6 group animate-fade-up"
                style={{ animationDelay: `${i * 40}ms`, opacity: 0 }}
                data-testid={`drill-${d.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="tag">{d.level.toUpperCase()}</span>
                  <span className="text-white/40 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> {d.duration_min}m</span>
                </div>
                <div className="font-oswald uppercase text-xl tracking-tight">{lang === "bg" ? d.title_bg : d.title_en}</div>
                <div className="label-mono mt-1">{d.category}</div>
                <p className="text-white/60 text-sm mt-3">{lang === "bg" ? d.description_bg : d.description_en}</p>
                {d.video_url && (
                  <div className="mt-4 aspect-video border border-white/10 overflow-hidden">
                    <iframe src={d.video_url} title={d.title_en} className="w-full h-full" allowFullScreen />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
